import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { AreaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro } from '../../nucleo/api';
import { nomeDeArquivo } from '../../nucleo/formato';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { CHAVE_CENTRAL, useCentral } from './CentralArquivos';
import { chaveDaOpcao, type OpcaoPortfolio } from './portfolio-envio';

/**
 * Lista de 03/11 (ficha `demandas/entrada/2026-11-03_malgo_FUN_portfolio-por-vertical-na-proposta.md`): em Configurações do CRM >
 * Preferências > Central de Arquivos, a seção "Portfólios" (uma linha por vertical mais o Malgo Geral, cada uma com o arquivo da
 * Central e "Sem arquivo" quando vazia; a opção passa a valer sem mudar código) e o texto "Conheça nossos outros serviços" do
 * e-mail da proposta. Quem não gere a Central, ou não vê todas as empresas, vê só leitura.
 */
// Prefixo de CHAVE_CENTRAL (invalidar a Central invalida também os portfólios); literal para não criar ciclo de módulos.
export const CHAVE_PORTFOLIOS = ['pipeline', 'central-arquivos', 'portfolios'] as const;
export const CHAVE_OUTROS_SERVICOS = ['pipeline', 'crm', 'outros-servicos'] as const;
export const MAX_OUTROS_SERVICOS = 500;

export interface PortfoliosResposta {
  itens: OpcaoPortfolio[];
  pode_gerir: boolean;
}

/** As opções de portfólio com o arquivo que a pessoa vê (a mesma consulta da Central e do envio da proposta). */
export function usePortfolios(habilitado = true) {
  return useQuery({ queryKey: CHAVE_PORTFOLIOS, queryFn: () => get<PortfoliosResposta>('/pipeline/central-arquivos/portfolios'), enabled: habilitado, staleTime: 30_000 });
}

/** O texto "Conheça nossos outros serviços" (vazio = não entra no e-mail). */
export function useOutrosServicos() {
  return useQuery({ queryKey: CHAVE_OUTROS_SERVICOS, queryFn: () => get<{ texto: string; padrao: string }>('/pipeline/crm/outros-servicos'), staleTime: 30_000 });
}

/** O corpo do PUT de uma opção. */
export const corpoDoPortfolio = (o: Pick<OpcaoPortfolio, 'tipo' | 'empresa_id'>, arquivoId: string | null) => (o.tipo === 'geral' ? { geral: true, arquivo_id: arquivoId } : { vertical_id: o.empresa_id, arquivo_id: arquivoId });

export function PortfoliosCentral() {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const q = usePortfolios();
  const central = useCentral();
  // rl0311-1 VIS-M1: nenhuma lista grava no onChange; a escolha de cada opção espera o Salvar da linha.
  const [rascunhos, setRascunhos] = useState<Record<string, string>>({});
  const salvar = useMutation({
    mutationFn: (v: { opcao: OpcaoPortfolio; arquivoId: string | null }) => api('PUT', '/pipeline/central-arquivos/portfolios', corpoDoPortfolio(v.opcao, v.arquivoId)),
    onSuccess: async (_r, v) => {
      toast.sucesso(t('crm.cfg.central.portfolioSalvo'));
      await qc.invalidateQueries({ queryKey: CHAVE_CENTRAL });
      setRascunhos((x) => Object.fromEntries(Object.entries(x).filter(([k]) => k !== chaveDaOpcao(v.opcao))));
    },
    onError: async (e) => {
      toast.erro(mensagemDeErro(e));
      await qc.invalidateQueries({ queryKey: CHAVE_CENTRAL });
    },
  });
  const nomeDaOpcao = (o: Pick<OpcaoPortfolio, 'tipo' | 'nome'>) => (o.tipo === 'geral' ? t('crm.cfg.central.portfolioGeral') : (o.nome ?? ''));

  return (
    <div className="flex min-w-0 flex-col gap-3" data-config-portfolios="">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold text-tinta-1">{t('crm.cfg.central.portfolioTitulo')}</h3>
        <p className="text-sm text-tinta-2">{t('crm.cfg.central.portfolioIntro')}</p>
      </div>
      <Estado consulta={q}>
        {(d) => {
          const arquivos = central.data?.itens ?? [];
          return (
            <>
            {/* rl0311-1 SPEC-B1: as opções aparecem em aberto mesmo com a Central vazia. */}
            {d.pode_gerir && central.isSuccess && !arquivos.length && <p className="text-sm text-tinta-3">{t('crm.cfg.central.portfolioSemArquivosNaCentral')}</p>}
            <ul className="flex flex-col divide-y divide-borda rounded-xl border border-borda bg-sup-1" data-portfolio-lista="">
              {d.itens.map((o) => {
                const chave = chaveDaOpcao(o);
                const nome = nomeDaOpcao(o);
                const rotuloArquivo = o.arquivo ? nomeDeArquivo(o.arquivo.nome) : t('crm.cfg.central.portfolioSem');
                // O rascunho só vale enquanto o arquivo existe na Central.
                const rascunho = rascunhos[chave];
                const escolhido = rascunho !== undefined && (rascunho === '' || arquivos.some((a) => a.id === rascunho)) ? rascunho : (o.arquivo?.id ?? '');
                // rl0311-2 VIS2-B2: o arquivo do rascunho pode ter virado portfólio de outra opção: aí não há o que salvar nesta.
                const escolhidoEmUso = !!escolhido && d.itens.some((x) => chaveDaOpcao(x) !== chave && x.arquivo?.id === escolhido);
                const mudou = escolhido !== (o.arquivo?.id ?? '') && !escolhidoEmUso;
                return (
                  <li key={chave} className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1.5 px-3 py-2.5" data-portfolio-opcao={chave}>
                    <span className="min-w-0 basis-full truncate text-sm font-medium sm:basis-44 sm:shrink-0" title={nome}>
                      {nome}
                    </span>
                    {d.pode_gerir ? (
                      <div className="flex min-w-0 basis-full items-center gap-2 sm:flex-1 sm:basis-0">
                        <div className="min-w-0 flex-1">
                        <Selecao
                          aria-label={t('crm.cfg.central.portfolioSelecionar', { nome })}
                          title={t('crm.cfg.central.portfolioSelecionar', { nome })}
                          value={escolhido}
                          disabled={salvar.isPending || central.isPending}
                          vazio={t('crm.cfg.central.portfolioSem')}
                          opcoes={arquivos.map((a) => {
                            // Um arquivo é o portfólio de uma opção só: o que já serve a outra fica indisponível aqui.
                            const dono = d.itens.find((x) => (x.tipo === 'geral' ? a.portfolio_geral : !!a.portfolio_empresa_id && x.empresa_id === a.portfolio_empresa_id));
                            const emUso = !!dono && chaveDaOpcao(dono) !== chave;
                            const rotulo = nomeDeArquivo(a.nome);
                            return { valor: a.id, rotulo: emUso ? t('crm.cfg.central.portfolioEmUso', { nome: rotulo, opcao: nomeDaOpcao(dono) }) : rotulo, desabilitado: emUso };
                          })}
                          onChange={(e) => setRascunhos((x) => ({ ...x, [chave]: e.target.value }))}
                          data-portfolio-seletor={chave}
                        />
                        </div>
                        {mudou && (
                          <Botao tamanho="sm" variante="secundario" disabled={salvar.isPending && chaveDaOpcao(salvar.variables.opcao) !== chave} carregando={salvar.isPending && chaveDaOpcao(salvar.variables.opcao) === chave} aria-label={t('crm.cfg.central.portfolioSalvarNome', { nome })} onClick={() => salvar.mutate({ opcao: o, arquivoId: escolhido || null })} data-portfolio-salvar={chave}>
                            {tn('comum.salvar')}
                          </Botao>
                        )}
                      </div>
                    ) : (
                      <span className={`min-w-0 basis-full truncate text-sm sm:flex-1 sm:basis-0 ${o.arquivo ? '' : 'text-tinta-3'}`} title={rotuloArquivo} data-portfolio-leitura={chave}>
                        {rotuloArquivo}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
            </>
          );
        }}
      </Estado>
    </div>
  );
}

/** Texto "Conheça nossos outros serviços" (Configurações do CRM): o de fábrica até alguém mudar; vazio = não entra no e-mail. */
export function OutrosServicosConfig() {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const s = useSessao();
  const q = useOutrosServicos();
  const pode = podeNoWorkspace(s, 'pipeline.configurar');
  const [rascunho, setRascunho] = useState<string | null>(null);
  const gravar = useMutation({
    mutationFn: (texto: string | null) => api('PUT', '/pipeline/crm/outros-servicos', { texto }),
    onSuccess: async () => {
      toast.sucesso(t('crm.cfg.central.outrosSalvo'));
      setRascunho(null);
      await qc.invalidateQueries({ queryKey: CHAVE_OUTROS_SERVICOS });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <div className="flex min-w-0 flex-col gap-3" data-config-outros-servicos="">
      <h3 className="text-base font-semibold text-tinta-1">{t('crm.cfg.central.outrosTitulo')}</h3>
      <Estado consulta={q}>
        {(d) => {
          const valor = rascunho ?? d.texto ?? '';
          return (
            <>
              <Rotulo id="central-outros-servicos" rotulo={t('crm.cfg.central.outrosCampo')} ajuda={t('crm.cfg.central.outrosAjuda', { n: valor.length })}>
                <AreaSensivel
                  id="central-outros-servicos"
                  rows={2}
                  maxLength={MAX_OUTROS_SERVICOS}
                  placeholder={t('crm.cfg.central.outrosPlaceholder')}
                  value={valor}
                  disabled={!pode}
                  onChange={(e) => setRascunho(e.target.value)}
                  data-outros-servicos-campo=""
                />
              </Rotulo>
              {pode && (
                <div className="flex flex-wrap gap-2">
                  <Botao variante="secundario" carregando={gravar.isPending} disabled={rascunho === null || rascunho === d.texto} onClick={() => gravar.mutate(valor)} data-outros-servicos-salvar="">
                    {tn('comum.salvar')}
                  </Botao>
                  <Botao variante="fantasma" disabled={gravar.isPending || (d.texto === d.padrao && rascunho === null)} onClick={() => gravar.mutate(null)} data-outros-servicos-padrao="">
                    {t('crm.cfg.central.outrosPadrao')}
                  </Botao>
                </div>
              )}
            </>
          );
        }}
      </Estado>
    </div>
  );
}
